<template>
  <div class="program_group_consumer_table">
    <v-data-table
      v-model="customers"
      :disable-sort="degraded"
      :headers="headers"
      hover
      :show-select="enableSelection"
      :items="parsedItems"
      item-value="key"
      :footer-props="{
        'items-per-page-options': [10, 25, 50, 100],
        'items-per-page-text': 'Patients par page',
        'show-current-page': true,
        'show-first-last-page': true
      }"
      @click:row="onRowClick"
    >
      <template #[`item.name`]="{ item }">
        <div class="flex flex-col">
          <p>
            {{ item.name }}
          </p>
          <p
            v-if="item.birthdate"
            class="text-sm text-gray-500"
          >
            {{ $filters.date(item.birthdate, 'short') }}
          </p>
        </div>
      </template>

      <template #[`item.groups`]="{ item }">
        <div>
          {{ item.groups }}
        </div>
      </template>
      <template #[`item.excluded`]="{ item }">
        <div
          v-for="(_excluded, index) in item.excluded"
          :key="_excluded"
          class="flex"
        >
          <div
            v-if="_excluded"
            class="flex items-center gap-1"
          >
            <p>
              {{ _excluded }}
              <span v-if="item.completed !== false">✅</span>
              <span v-else>⚠️</span>
            </p>
          </div>
          <div
            v-if="index !== item.excluded?.length - 1"
            class="bg-gray-300 my-1"
            style="height: 1px"
          />
        </div>
      </template>

      <template
        v-for="column in trackerColumns"
        #[`item.${column.key}`]="{ item }"
        :key="column.key"
      >
        <v-tooltip
          v-if="(item[column.key] as TrackerColumn)?.tooltip"
        >
          <template #activator="{ props }">
            <span v-bind="props" class="cursor-help whitespace-pre-line">
              <StatusDotText :text="(item[column.key] as TrackerColumn).value?.toString() ?? ''" />
            </span>
          </template>
          <span class="whitespace-pre-line">
            {{ (item[column.key] as TrackerColumn).tooltip }}
          </span>
        </v-tooltip>
        <div v-else class="whitespace-pre-line">
          <StatusDotText :text="(item[column.key] as TrackerColumn)?.value?.toString() || ''" />
        </div>
      </template>

      <template #[`footer.page-text`]="{ pageStart, pageStop, itemsLength }">
        <span v-if="itemsLength > 0">
          Affichage de {{ pageStart }} à {{ pageStop }} sur {{ itemsLength }} patients
        </span>
      </template>
    </v-data-table>
  </div>
</template>

<script lang="ts" setup>
import {computed, ref, watch} from "vue";
import dateHelper from '@/core/helpers/dateHelper';
import StatusDotText from "@/core/components/ui/badge/StatusDotText.vue";
import {SupportProgramMeta, SupportProgramPatient} from "@/program/interfaces/Program";
import dayjs from "dayjs";
import {storeToRefs} from "pinia";
import {useCommunicationStore} from "@/communication/stores/communication.store";
import useProgramConsumerSort from "@/program/composables/useProgramConsumerSort";

interface ConsumerItem {
    key: string;
    name: string;
    birthdate: string;
    groups: string;
    excluded: string[];
    completed: boolean;

    [tracker: string]: string | boolean | string[] | undefined | TrackerColumn;
}

interface TrackerColumn {
    /** The title of the tracker */
    label: string;
    /** The tracker value */
    value?: string;
    /** The action associated with the tracker */
    action?: string;
    /** The tooltip to display, if any. */
    tooltip?: string;
}

const {programCustomers} = storeToRefs(useCommunicationStore());

const {consumers, meta, enableSelection = false, degraded = false} = defineProps<{
    consumers: SupportProgramPatient[];
    meta: SupportProgramMeta;
    enableSelection?: boolean;
    degraded?: boolean;
}>();

const emits = defineEmits<{ (e: 'consumerClick', consumer: SupportProgramPatient): void; }>();
const {sort} = useProgramConsumerSort();

const customers = ref<string[]>([]);

/**
 * The data table headers.
 */
const headers = computed((): { title: string; key: string }[] => {
    const columns: { title: string; key: string; sort?: (a, b) => number }[] = [];

    columns.push({
        title: `Patients ${degraded ? '' : '(' + parsedItems.value.length + ')'}`,
        key: 'name',
        sort
    });

    if (Object.keys(meta.included).length > 1) {
        columns.push({
            title: includedColumnLabel.value,
            key: 'groups',
            sort
        });
    }

    if (!meta.options.hideExclusionColumn) {
        columns.push({
            title: excludedColumnLabel.value,
            key: 'excluded',
            sort
        });
    }

    trackerColumns.value.forEach((trackerColumn: { title: string, key: string }) => {
        columns.push({
            title: trackerColumn.title,
            key: trackerColumn.key,
            sort: (a, b) => sort(a?.value, b?.value)
        });
    });

    pathwayProgressColumns.value.forEach((progressColumn) => {
        columns.push(progressColumn);
    });

    return columns;
});

/**
 * Parse the consumers items for the data table.
 */
const parsedItems = computed(() => {
    return consumers.map(consumer => {
        if (enableSelection && !consumer.phone) {
            return;
        }
        return {
            key: consumer.key,
            name: consumer.name,
            birthdate: consumer.birthdate,
            groups: groupLabels(consumer),
            completed: consumer.completed,
            excluded: aggregatedExcludedValues(consumer),
            ...consumer.tracker?.columns?.reduce((trackerColumns, tracker) => {
                if (dayjs(tracker.value, 'YYYY-MM-DD', true).isValid()) {
                    tracker.value = dayjs(tracker.value).format('DD/MM/YYYY');
                }

                trackerColumns[tracker.action || tracker.label] = tracker;

                return trackerColumns;
            }, {}),
            ...pathwayProgressConsumerColumns(consumer)
        };
    }).filter((consumer): consumer is ConsumerItem => !!consumer);
});

/**
 * The tracker columns for the data table.
 */
const trackerColumns = computed((): { title: string, key: string }[] => {
    return consumers.reduce((trackerColumns: { title: string, key: string }[], consumer) => {
        if (!consumer.tracker?.columns) {
            return trackerColumns;
        }

        consumer.tracker.columns.forEach(column => {
            if (trackerColumns.find(
                trackerColumn => trackerColumn.key === column.action || trackerColumn.key === column.label
            )) {
                return;
            }

            trackerColumns.push({
                title: column.label,
                key: column.action || column.label
            });
        });

        return trackerColumns;
    }, []);
});

/**
 * The pathway progress columns for the data table.
 */
const pathwayProgressColumns = computed((): { title: string; key: string }[] => {
    return meta.progress?.map((progress, index) => ({
        title: progress,
        key: 'progress' + index,
    })) ?? [];
});

/**
 * The excluded column label.
 */
const excludedColumnLabel = computed((): string => {
    return meta?.labels?.excluded ?? 'Date';
});

/**
 * The included column label.
 */
const includedColumnLabel = computed((): string => {
    return 'Cibles';
});

/**
 * Whether to aggregate excluded labels.
 */
const shouldAggregateExcludedLabels = computed((): boolean => {
    return Object.keys(meta.excluded).length > 1;
});

watch(() => customers.value, (value) => {
    programCustomers.value = value;
}, {immediate: true});

/**
 * Get the group labels for a consumer.
 */
const groupLabels = (consumer: SupportProgramPatient): string => {
    let groups = '';
    consumer.groups.included?.forEach(group => {
        if (Object.hasOwn(meta.included, group.slug)) {
            if (!groups) {
                groups = meta.included[group.slug];
            } else {
                groups += ' | ' + meta.included[group.slug];
            }
        }
    });

    return groups;
};

/** Format a date string to locale short format. */
const localeDate = (dateString: string) => {
    return dateHelper.shortFormat(new Date(dateString));
};

/** Handle row click event. */
const onRowClick = (_: Event, row: { item: SupportProgramPatient }) => {
    const consumer = consumers.find(consumer => consumer.key === row.item.key);

    if (consumer && !enableSelection) {
        emits('consumerClick', consumer);
    }
};

/**
 * Aggregate excluded values for a consumer.
 */
const aggregatedExcludedValues = (consumer: SupportProgramPatient): string[] => {
    return consumer.groups.excluded.map((group) => {
        const text = meta.excluded[group.slug] ?? null;
        const columnValue = localeDate(consumer.last_visits.excluded);

        if (!shouldAggregateExcludedLabels.value) {
            return columnValue;
        }

        return `${text} (${columnValue})`;
    });
};

/**
 * Get pathway progress columns for a consumer.
 */
const pathwayProgressConsumerColumns = (consumer: SupportProgramPatient) => {
    const progresses: { [key: string]: string } = {};

    meta.progress?.forEach((_, index) => {
        progresses['progress' + index] = consumer.pathway?.progress[index]
            ? (consumer.pathway?.progress[index] as string).toString()
            : '-';
    });

    return progresses;
};
</script>
